import React, { useEffect, useState } from 'react';
import { Modal, Form, Select, DatePicker, Input, Button, Tabs, Table, Tag } from 'antd';
import { useExcuses } from '../../../hooks/useExcuse';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { TextArea } = Input;
const { Option } = Select;

const ExcuseRequestModal = ({ visible, onCancel, employee }) => {
  const [form] = Form.useForm();
  const [activeTab, setActiveTab] = useState('create');
  const [messageerror, setMessageError] = useState("");
  const { 
    useGetExcuses, 
    useGetAssignments, 
    useCreateExcuseRequest,
    useGetExcuseRequests 
  } = useExcuses();
  
  const { data: allExcuses = [], isLoading: loadingExcuses } = useGetExcuses();
  const { data: assignments = [], isLoading: loadingAssignments } = useGetAssignments(
    employee?.id
  );
  const { data: excuseRequests = [], isLoading: loadingRequests } = useGetExcuseRequests(
   {employeeId: employee?.id}
  );
  
  const createRequestMutation = useCreateExcuseRequest();

  useEffect(() => {
    if (!visible) {
      form.resetFields();
      setActiveTab('create');
    }
  }, [visible, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const [startDate, endDate] = values.dateRange;

      const requestData = {
        employeeId: employee.id,
        excuseId: values.excuseId,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        reason: values.reason || '',
      };

      await createRequestMutation.mutateAsync(requestData);
      form.resetFields();
      setMessageError("")
      onCancel();
    } catch (err) {
      setMessageError(err.response?.data[0] || "Failed to create excuse request");
      console.error("Validation failed:", err);
    }
  };

  const selectedExcuseId = Form.useWatch('excuseId', form);
  const selectedAssignment = assignments.find(a => a.excuseId === selectedExcuseId);

  // Get excuses that are assigned to this employee
  const assignedExcuses = allExcuses.filter(excuse =>
    assignments.some(assignment => assignment.excuseId === excuse.id)
  );

  // Status mapping
  const getStatusTag = (status) => {
    const statusMap = {
      0: { text: 'Pending', color: 'orange' },
      1: { text: 'Approved', color: 'green' },
      2: { text: 'Rejected', color: 'red' }
    };
    const statusInfo = statusMap[status] || { text: 'Unknown', color: 'default' };
    return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
  };
const handleCancel = () => {  
  form.resetFields();
  setMessageError("");
  onCancel();
}
  // Table columns for excuse requests
  const columns = [
    {
      title: 'Excuse Type',
      dataIndex: 'excuseName',
      key: 'excuseName',
    },
    {
      title: 'Start Date',
      dataIndex: 'startDate',
      key: 'startDate',
      render: (date) => dayjs(date).format('YYYY-MM-DD'),
    },
    {
      title: 'End Date',
      dataIndex: 'endDate',
      key: 'endDate',
      render: (date) => dayjs(date).format('YYYY-MM-DD'),
    },
    {
      title: 'Days',
      dataIndex: 'daysRequested',
      key: 'daysRequested',
    },
    {
      title: 'Reason',
      dataIndex: 'reason',
      key: 'reason',
      ellipsis: true,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => getStatusTag(status),
    },
  ];

  const tabItems = [
    {
      key: 'create',
      label: 'Create Request',
      children: (
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            excuseId: undefined,
            dateRange: undefined,
            reason: '',
          }}
        >
          <Form.Item
            label="Excuse Type"
            name="excuseId"
            rules={[{ required: true, message: 'Please select an excuse type' }]}
          >
            <Select
              placeholder="Select excuse type"
              loading={loadingExcuses || loadingAssignments}
            >
              {assignedExcuses.map(excuse => (
                <Option key={excuse.id} value={excuse.id}>
                  {excuse.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {selectedAssignment && (
            <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-gray-700 mb-1">
                <strong>Allowed Days:</strong> {selectedAssignment.allowedDays}
              </p>
              <p className="text-sm text-gray-700 mb-1">
                <strong>Used Days:</strong> {selectedAssignment.usedDays || 0}
              </p>
              <p className="text-sm text-gray-700">
                <strong>Remaining:</strong> {selectedAssignment.allowedDays - (selectedAssignment.usedDays || 0)}
              </p>
            </div>
          )}

          <Form.Item
            label="Date Range"
            name="dateRange"
            rules={[{ required: true, message: 'Please select date range' }]}
          >
            <RangePicker
              className="w-full"
              disabledDate={(current) => {
                return current && current < dayjs().startOf('day');
              }}
            />
          </Form.Item>

          <Form.Item
            label="Reason"
            name="reason"
          >
            <TextArea
              rows={4}
              placeholder="Enter reason for excuse request (optional)"
            />
          </Form.Item>

          {assignedExcuses.length === 0 && !loadingExcuses && !loadingAssignments && (
            <div className="text-center p-4 bg-yellow-50 rounded-lg border border-yellow-200">
              <p className="text-yellow-700">
                This employee has no excuse assignments. Please assign excuses first.
              </p>
            </div>
          )}
          {messageerror && (
            <div className="text-center p-4 bg-red-50 rounded-lg border border-red-200 mt-4">
              <p className="text-red-700">
                {messageerror}
              </p>
            </div>
          ) }
        </Form>
      ),
    },
    {
      key: 'history',
      label: 'Request History',
      children: (
        <Table
          columns={columns}
          dataSource={excuseRequests}
          loading={loadingRequests}
          rowKey="id"
          pagination={{
            pageSize: 5,
            showSizeChanger: false,
          }}
          locale={{
            emptyText: 'No excuse requests found for this employee',
          }}
        />
      ),
    },
  ];

  return (
    <Modal
      title={`Excuse Request - ${employee?.fullName}`}
      open={visible}
      onCancel={handleCancel}
      footer={
        activeTab === 'create' ? [
          <Button key="cancel" onClick={onCancel}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type=""
            onClick={handleSubmit}
            loading={createRequestMutation.isLoading}
            disabled={assignedExcuses.length === 0}
            className='bg-primary text-white hover:bg-primary_hover'
          >
            Submit Request
          </Button>,
        ] : [
          <Button key="close" onClick={onCancel}>
            Close
          </Button>,
        ]
      }
      destroyOnClose
      width={800}
    >
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
      />
    </Modal>
  );
};

export default ExcuseRequestModal;